import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from 'ApplicationContext';
import { AuditLogEntry } from 'apiClient/Api';
import { useDocumentTitle } from 'Hooks/useDocumentTitle';
import { useSearchParams } from 'react-router';
import { HubConnectionBuilder, HubConnectionState, LogLevel, HttpTransportType } from '@microsoft/signalr';

const AUDIT_LOG_HUB_PATH = '/hubs/audit-log';
const AUDIT_LOG_REGISTER_METHOD = 'RegisterAuditLog';
const AUDIT_LOG_UNREGISTER_METHOD = 'UnregisterAuditLog';
const AUDIT_LOG_EVENT_NAMES = ['auditlogcreated']; // SignalR .NET converts to lowercase

/**
 * Admin page for searching and viewing audit log entries.
 * @returns {JSX.Element} The audit log page.
 */
export const AdminAuditLog: React.FC = (): JSX.Element => {
  useDocumentTitle('base.pages.auditLog');

  const { t } = useTranslation();
  const context = useApplicationContext();
  const { api, apiError } = context;
  const [searchParams, setSearchParams] = useSearchParams();

  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [referenceTypeInput, setReferenceTypeInput] = useState<string>('');
  const [referenceIdInput, setReferenceIdInput] = useState<string>('');
  const [realtimeConnected, setRealtimeConnected] = useState<boolean>(false);

  /**
   * Loads audit entries using optional reference filters.
   * @param {string | undefined} referenceType Optional logical reference type.
   * @param {string | undefined} referenceId Optional reference identifier.
   * @returns {Promise<void>} Promise resolved when loading is complete.
   */
  const loadAuditEntries = useCallback(async (referenceType?: string, referenceId?: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.auditLog.auditLogGetAuditEntries({
        referenceType: referenceType || null,
        referenceId: referenceId || null,
      });
      setEntries(response.data ?? []);
    } catch (err: any) {
      setEntries([]);
      setError(err?.response?.data?.detail || t('auditLog.error'));
      apiError(err);
    } finally {
      setLoading(false);
    }
  }, [api.auditLog, apiError, t]);

  useEffect(() => {
    const referenceType = searchParams.get('referenceType')?.trim() ?? '';
    const referenceId = searchParams.get('referenceId')?.trim() ?? '';

    setReferenceTypeInput(referenceType);
    setReferenceIdInput(referenceId);
    loadAuditEntries(referenceType || undefined, referenceId || undefined);
  }, [loadAuditEntries, searchParams]);

  /**
   * Applies the current filter inputs to the audit log query.
   */
  const handleApplyFilters = (): void => {
    const nextParams = new URLSearchParams();
    const referenceType = referenceTypeInput.trim();
    const referenceId = referenceIdInput.trim();

    if (referenceType) {
      nextParams.set('referenceType', referenceType);
    }
    if (referenceId) {
      nextParams.set('referenceId', referenceId);
    }

    setSearchParams(nextParams);
  };

  /**
   * Clears all filter inputs and reloads unfiltered audit entries.
   */
  const handleClearFilters = (): void => {
    setReferenceTypeInput('');
    setReferenceIdInput('');
    setSearchParams(new URLSearchParams());
  };

  /**
   * Refreshes audit entries using the currently entered filter values.
   */
  const handleRefresh = (): void => {
    loadAuditEntries(referenceTypeInput.trim() || undefined, referenceIdInput.trim() || undefined);
  };

  const columns = useMemo<GridColDef<AuditLogEntry>[]>(() => [
    {
      field: 'timestamp',
      headerName: t('auditLog.columns.timestamp'),
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<AuditLogEntry, string | undefined>) => (
        <Typography variant="body2">
          {params.value ? dayjs(params.value).format('DD.MM.YYYY HH:mm:ss') : '-'}
        </Typography>
      ),
    },
    {
      field: 'user',
      headerName: t('auditLog.columns.user'),
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => row.user ?? '-',
    },
    {
      field: 'referenceType',
      headerName: t('auditLog.columns.referenceType'),
      flex: 1,
      minWidth: 140,
      valueGetter: (_, row) => row.referenceType ?? '-',
    },
    {
      field: 'referenceId',
      headerName: t('auditLog.columns.referenceId'),
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => row.referenceId ?? '-',
    },
    {
      field: 'function',
      headerName: t('auditLog.columns.function'),
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => row.function ?? '-',
    },
    {
      field: 'message',
      headerName: t('auditLog.columns.message'),
      flex: 1.3,
      minWidth: 220,
      valueGetter: (_, row) => row.message ?? '-',
    },
    {
      field: 'details',
      headerName: t('auditLog.columns.details'),
      flex: 1.3,
      minWidth: 220,
      valueGetter: (_, row) => row.details ?? '-',
    },
    {
      field: 'clientIp',
      headerName: t('auditLog.columns.clientIp'),
      flex: 1,
      minWidth: 140,
      valueGetter: (_, row) => row.clientIp ?? '-',
    },
    {
      field: 'userAgent',
      headerName: t('auditLog.columns.userAgent'),
      flex: 1.4,
      minWidth: 240,
      valueGetter: (_, row) => row.userAgent ?? '-',
    },
  ], [t]);

  /**
   * Resolves a stable row identifier for the audit grid.
   * @param {AuditLogEntry} row The current audit row.
   * @returns {string} Stable identifier string.
   */
  const getRowId = useCallback((row: AuditLogEntry): string => {
    return row.id ?? `${row.timestamp ?? 'unknown'}-${row.referenceId ?? 'na'}-${row.function ?? 'na'}`;
  }, []);

  /**
   * Checks whether an audit entry matches currently active filters.
   * @param {AuditLogEntry} entry The incoming entry to validate.
   * @param {string} referenceType Active reference type filter.
   * @param {string} referenceId Active reference id filter.
   * @returns {boolean} True when entry matches all active filters.
   */
  const matchesActiveFilters = useCallback((entry: AuditLogEntry, referenceType: string, referenceId: string): boolean => {
    const hasReferenceTypeFilter = referenceType.length > 0;
    const hasReferenceIdFilter = referenceId.length > 0;

    const matchesReferenceType = !hasReferenceTypeFilter || (entry.referenceType ?? '').toLowerCase() === referenceType.toLowerCase();
    const matchesReferenceId = !hasReferenceIdFilter || (entry.referenceId ?? '').toLowerCase() === referenceId.toLowerCase();

    return matchesReferenceType && matchesReferenceId;
  }, []);

  /**
   * Upserts a new audit entry at the top while preventing duplicates.
   * @param {AuditLogEntry} entry New entry pushed by SignalR.
   */
  const insertRealtimeEntry = useCallback((entry: AuditLogEntry): void => {
    setEntries((prevEntries) => {
      const exists = prevEntries.some((current) => getRowId(current) === getRowId(entry));
      if (exists) {
        return prevEntries;
      }

      return [entry, ...prevEntries];
    });
  }, [getRowId]);

  useEffect(() => {
    if (!context.token) {
      console.log('[AdminAuditLog] No auth token available, skipping SignalR connection');
      setRealtimeConnected(false);
      return;
    }

    const referenceType = searchParams.get('referenceType')?.trim() ?? '';
    const referenceId = searchParams.get('referenceId')?.trim() ?? '';

    console.log('[AdminAuditLog] Setting up SignalR connection with filters:', { referenceType, referenceId });

    const connection = new HubConnectionBuilder()
      .withUrl(`${context.baseUrl}${AUDIT_LOG_HUB_PATH}`, {
        accessTokenFactory: () => context.token ?? '',
        transport: HttpTransportType.WebSockets, // Use WebSocket only to avoid CORS and auth issues
        skipNegotiation: true, // Skip negotiate phase since we only use WebSocket
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    /**
     * Handles new audit log entries received over SignalR.
     * @param {AuditLogEntry} entry The pushed audit log entry.
     */
    const handleIncomingEntry = (entry: AuditLogEntry): void => {
      if (!matchesActiveFilters(entry, referenceType, referenceId)) {
        return;
      }

      insertRealtimeEntry(entry);
    };

    AUDIT_LOG_EVENT_NAMES.forEach((eventName) => {
      connection.on(eventName, handleIncomingEntry);
    });

    connection.onclose(() => {
      console.log('[AdminAuditLog] SignalR connection closed');
      setRealtimeConnected(false);
    });

    connection.onreconnected(async () => {
      console.log('[AdminAuditLog] SignalR reconnected');
      try {
        await connection.invoke(AUDIT_LOG_REGISTER_METHOD);
        console.log('[AdminAuditLog] RegisterAuditLog invoked after reconnect');
        setRealtimeConnected(true);
      } catch (err: any) {
        console.error('[AdminAuditLog] RegisterAuditLog failed after reconnect:', err);
        setRealtimeConnected(false);
      }
    });

    connection.onreconnecting((error?: Error) => {
      console.log('[AdminAuditLog] SignalR reconnecting...', error?.message);
      setRealtimeConnected(false);
    });

    let aborted = false;

    const connect = async (): Promise<void> => {
      try {
        const hubUrl = `${context.baseUrl}${AUDIT_LOG_HUB_PATH}`;
        console.log('[AdminAuditLog] Connecting to SignalR hub:', hubUrl);
        await connection.start();
        
        if (aborted) {
          console.log('[AdminAuditLog] Connection started but was aborted, stopping...');
          await connection.stop();
          return;
        }

        console.log('[AdminAuditLog] SignalR connection started');
        await connection.invoke(AUDIT_LOG_REGISTER_METHOD);
        console.log('[AdminAuditLog] RegisterAuditLog invoked');
        setRealtimeConnected(true);
      } catch (err: any) {
        if (aborted) {
          console.log('[AdminAuditLog] Connection error after abort (expected):', err.message);
          return;
        }
        console.error('[AdminAuditLog] SignalR connection error:', err);
        console.error('[AdminAuditLog] Error details:', {
          message: err.message,
          statusCode: err.statusCode,
          statusText: err.statusText,
        });
        setRealtimeConnected(false);
      }
    };

    connect();

    return () => {
      aborted = true;
      const disconnect = async (): Promise<void> => {
        try {
          if (connection.state === HubConnectionState.Connected) {
            await connection.invoke(AUDIT_LOG_UNREGISTER_METHOD);
          }
        } finally {
          await connection.stop();
          setRealtimeConnected(false);
        }
      };

      disconnect();
    };
  }, [context.baseUrl, context.token, searchParams, matchesActiveFilters, insertRealtimeEntry]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1">{t('auditLog.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('auditLog.subtitle')}</Typography>
          <Typography variant="caption" color={realtimeConnected ? 'success.main' : 'text.secondary'}>
            {realtimeConnected ? t('auditLog.live.connected') : t('auditLog.live.disconnected')}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={handleRefresh}>{t('auditLog.actions.refresh')}</Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('auditLog.filters.referenceType')}
            value={referenceTypeInput}
            onChange={(event) => setReferenceTypeInput(event.target.value)}
          />
          <TextField
            fullWidth
            label={t('auditLog.filters.referenceId')}
            value={referenceIdInput}
            onChange={(event) => setReferenceIdInput(event.target.value)}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button variant="contained" onClick={handleApplyFilters}>{t('auditLog.filters.apply')}</Button>
            <Button variant="text" onClick={handleClearFilters}>{t('auditLog.filters.clear')}</Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <DataGrid
        autoHeight
        loading={loading}
        rows={entries}
        columns={columns}
        getRowId={getRowId}
        rowHeight={40}
        columnHeaderHeight={44}
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
        localeText={{
          noRowsLabel: t('auditLog.noRows'),
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            alignItems: 'center',
          },
          '& .MuiDataGrid-cellContent': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
};
